import { Box, Button, ButtonProps, Flex, Icon, Text } from '@chakra-ui/react';
import Download from '@mui/icons-material/Download';
import TaskAlt from '@mui/icons-material/TaskAlt';
import { useState } from 'react';

export type DownloadState = 'idle' | 'downloading' | 'completed';

interface DownloadButtonProps extends Omit<ButtonProps, 'onClick'> {
  filename: string;
  fileExtension?: string;
  onDownload: () => Promise<void>;
  downloadingText?: string;
  completedText?: string;
  resetAfter?: number;
}

const LoadingDots = () => {
  return (
    <Flex ml={2} alignItems="center">
      <Box
        className="dot"
        w="4px"
        h="4px"
        borderRadius="full"
        bg="gray.400"
        mx="1px"
        sx={{
          animation: 'dotFade 1.4s infinite',
          animationDelay: '0s',
        }}
      />
      <Box
        className="dot"
        w="4px"
        h="4px"
        borderRadius="full"
        bg="gray.400"
        mx="1px"
        sx={{
          animation: 'dotFade 1.4s infinite',
          animationDelay: '0.2s',
        }}
      />
      <Box
        className="dot"
        w="4px"
        h="4px"
        borderRadius="full"
        bg="gray.400"
        mx="1px"
        sx={{
          animation: 'dotFade 1.4s infinite',
          animationDelay: '0.4s',
        }}
      />
      <style>{`
        @keyframes dotFade {
          0%,
          60%,
          100% {
            transform: scale(0.7);
            opacity: 0.3;
          }
          30% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </Flex>
  );
};

export const fakeDownloadExcel = async (
  filename: string,
  data?: any
): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 1500));

  // TODO :  change with real data download excel
  const fakeData = data || { items: [{ id: 1, name: 'Test Item' }] };
  const blob = new Blob([JSON.stringify(fakeData)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const DownloadButton = ({
  filename,
  fileExtension = '.xlsx',
  onDownload,
  downloadingText = 'Mengunduh',
  completedText = 'Selesai',
  resetAfter = 3000,
  ...buttonProps
}: DownloadButtonProps) => {
  const [downloadState, setDownloadState] = useState<DownloadState>('idle');

  const handleDownload = async () => {
    // TODO : change with real data download excel
    if (downloadState !== 'idle') return;

    setDownloadState('downloading');

    try {
      await onDownload();
      setDownloadState('completed');

      // Reset to idle state after specified duration
      if (resetAfter) {
        setTimeout(() => {
          setDownloadState('idle');
        }, resetAfter);
      }
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadState('idle');
    }
  };

  return (
    <Button
      variant="link"
      fontSize="sm"
      alignContent="center"
      _hover={{ opacity: 0.6 }}
      onClick={handleDownload}
      isDisabled={downloadState === 'downloading'}
      {...buttonProps}
    >
      {downloadState === 'idle' && (
        <>
          <Icon as={Download} fontSize="14px" my="auto" color="blue" />
          <Text ml={1} color="blue">
            Unduh{fileExtension}
          </Text>
        </>
      )}

      {downloadState === 'downloading' && (
        <Flex align="center">
          <LoadingDots />
          <Text>{downloadingText}</Text>
        </Flex>
      )}

      {downloadState === 'completed' && (
        <Flex align="center" color="green">
          <Icon as={TaskAlt} fontSize="14px" />
          <Text ml={1}>{completedText}</Text>
        </Flex>
      )}
    </Button>
  );
};
